import React from 'react';
import PropTypes from 'prop-types';
import { cx, css } from 'emotion';
import ThemeContext from '@weave-design/theme-context';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

const AVAILABLE_ALIGNMENTS = Object.freeze(["center", "justify", "left", "right"]);
const AVAILABLE_FONT_WEIGHTS = Object.freeze([300, 400, 500, 600, 700, "bold", // same as 700
"normal" // same as 400
]);
const AVAILABLE_VARIANTS = Object.freeze(["body", "caption", "h1", "h2", "h3"]);

function stylesheet(props, themeData) {
  const {
    stylesheet: customStylesheet,
    fontWeight,
    align
  } = props;
  const variant = props.variant || "body";
  const styles = {
    typography: {
      color: themeData[`typography.${variant}.color`],
      display: "block",
      fontFamily: themeData[`typography.${variant}.fontFamily`],
      fontSize: themeData[`typography.${variant}.fontSize`],
      fontWeight: fontWeight || themeData[`typography.${variant}.fontWeight`],
      lineHeight: themeData[`typography.${variant}.lineHeight`],
      margin: 0,
      textAlign: align || "initial"
    }
  };
  return customStylesheet ? customStylesheet(styles, props, themeData) : styles;
}

const _excluded = ["align", "children", "fontWeight", "variant", "stylesheet", "elementType", "typographyRef"];

const Typography = props => {
  const onElementType = () => {
    const {
      elementType,
      variant
    } = props;

    if (elementType) {
      return elementType;
    }

    return ["h1", "h2", "h3"].includes(variant) ? variant : "p";
  };

  const {
    align,
    children,
    fontWeight,
    variant,
    stylesheet: customStylesheet,
    elementType,
    // we don't want this included in the otherProps that appear in the DOM
    typographyRef
  } = props,
        otherProps = _objectWithoutProperties(props, _excluded);

  const {
    className
  } = otherProps;
  return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, _ref => {
    let {
      resolvedRoles
    } = _ref;
    const styles = stylesheet(_objectSpread2({
      stylesheet: customStylesheet,
      align,
      fontWeight,
      variant
    }, props), resolvedRoles);
    const ElementType = onElementType();
    return /*#__PURE__*/React.createElement(ElementType, _extends({}, otherProps, {
      className: cx(css(styles.typography), className),
      ref: typographyRef
    }), children);
  });
};

Typography.displayName = "Typography";
Typography.propTypes = {
  /**
   * Sets the horizontal alignment of the text
   */
  align: PropTypes.oneOf(AVAILABLE_ALIGNMENTS),

  /**
   * Enables specifying the semantic element to be rendered by the component
   * If this prop is not provided, the semantic element will default to matching the variant (e.g. if the component
   * variant is h1, the element will be `<h1>`) or if there is no semantic element matching the variant, it
   * will default to `<p>`. You can provide elementType as a string, like "figcaption", or as a function, like
   * `({children}) => (<figcaption>{children}</figcaption>)`.
   */
  elementType: PropTypes.node,

  /**
   * Text to render
   */
  children: PropTypes.node,

  /**
   * Specifies the weight (or boldness) of the font
   */
  fontWeight: PropTypes.oneOf(AVAILABLE_FONT_WEIGHTS),

  /**
   * A callback ref that gets passed to the root HTML element
   */
  typographyRef: PropTypes.func,

  /**
   * Indicates the initial Typography style
   */
  variant: PropTypes.oneOf(AVAILABLE_VARIANTS),

  /**
   * Adds custom/overriding styles
   */
  stylesheet: PropTypes.func
};
Typography.__docgenInfo = {
  "description": "",
  "displayName": "Typography",
  "props": {
    "align": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_ALIGNMENTS"
      },
      "required": false,
      "description": "Sets the horizontal alignment of the text"
    },
    "elementType": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Enables specifying the semantic element to be rendered by the component\nIf this prop is not provided, the semantic element will default to matching the variant (e.g. if the component\nvariant is h1, the element will be `<h1>`) or if there is no semantic element matching the variant, it\nwill default to `<p>`. You can provide elementType as a string, like \"figcaption\", or as a function, like\n`({children}) => (<figcaption>{children}</figcaption>)`."
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Text to render"
    },
    "fontWeight": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_FONT_WEIGHTS"
      },
      "required": false,
      "description": "Specifies the weight (or boldness) of the font"
    },
    "typographyRef": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "A callback ref that gets passed to the root HTML element"
    },
    "variant": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_VARIANTS"
      },
      "required": false,
      "description": "Indicates the initial Typography style"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Adds custom/overriding styles"
    }
  }
};

export { AVAILABLE_ALIGNMENTS, AVAILABLE_FONT_WEIGHTS, AVAILABLE_VARIANTS, Typography as default };
